import { IDIOMAS, type Idioma } from './traducir';

const LLAVE = 'tallerya.idioma';

function esIdiomaConocido(valor: string): valor is Idioma {
  return (IDIOMAS as readonly string[]).includes(valor);
}

/**
 * Lee el idioma guardado, si hay uno y sigue siendo válido.
 *
 * Devuelve `null` —y no un idioma por omisión— para que quien llama pueda
 * distinguir «esta persona eligió español» de «esta persona no eligió nada».
 * Esa diferencia importa: lo segundo se resuelve mirando el navegador.
 */
export function idiomaGuardado(): Idioma | null {
  try {
    const guardado = localStorage.getItem(LLAVE);
    return guardado && esIdiomaConocido(guardado) ? guardado : null;
  } catch {
    // En una ventana privada, o con el almacenamiento bloqueado, esto lanza.
    // Que no se pueda recordar la elección no es motivo para no mostrar el sitio.
    return null;
  }
}

export function guardarIdioma(idioma: Idioma): void {
  try {
    localStorage.setItem(LLAVE, idioma);
  } catch {
    // Igual que arriba: se pierde entre visitas y el sitio sigue funcionando.
  }
}

/**
 * El idioma del navegador, si lo entendemos.
 *
 * `navigator.languages` viene ordenado por preferencia, así que se recorre en
 * ese orden. Se compara solo la primera parte —`es` de `es-GT`— porque el
 * producto traduce por idioma y no por país: a alguien con el navegador en
 * `es-MX` le sirve el mismo español.
 */
export function idiomaDelNavegador(): Idioma | null {
  const preferidos = typeof navigator === 'undefined' ? [] : (navigator.languages ?? []);
  for (const etiqueta of preferidos) {
    const base = etiqueta.split('-')[0]?.toLowerCase();
    if (base && esIdiomaConocido(base)) {
      return base;
    }
  }
  return null;
}

/**
 * El idioma con el que arranca la sesión.
 *
 * El orden es el que se acordó: **gana lo que la persona eligió**, después lo
 * que dice su navegador, y si nada de eso sirve, español — que es el idioma en
 * el que está escrito el producto.
 */
export function idiomaInicial(): Idioma {
  return idiomaGuardado() ?? idiomaDelNavegador() ?? 'es';
}
